import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Circle, ArrowRight } from 'lucide-react';

export function OnboardingDemo() {
  const onboardingSteps = [
    {
      step: 1,
      title: 'Personal Info',
      description: 'Basic information and contact details',
      path: '/onboarding/personal-info',
      features: ['Form validation', 'Required fields', 'Phone number formatting'],
    },
    {
      step: 2,
      title: 'Terms & Conditions',
      description: 'Review and accept CareCal terms before continuing',
      path: '/onboarding/terms',
      features: ['Inline terms review', 'Scroll-to-bottom requirement', 'Required acceptance'],
    },
  ];

  return (
    <div className="min-h-screen bg-cream py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl font-bold text-gray-900 mb-4">
            Onboarding Layout Demo
          </h1>
          <p className="font-body text-xl text-gray-600 max-w-2xl mx-auto">
            Experience the complete onboarding flow with step validation, data preservation,
            and responsive design.
          </p>
        </div>

        <div className="grid gap-6 mb-8">
          {onboardingSteps.map((step) => (
            <Card key={step.step} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blush-100 text-blush-600 font-semibold">
                      {step.step}
                    </div>
                    <div>
                      <CardTitle className="font-display text-xl">
                        Step {step.step}: {step.title}
                      </CardTitle>
                      <p className="font-body text-gray-600 mt-1">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  <Button asChild>
                    <Link to={step.path} className="flex items-center space-x-2">
                      <span>Try it</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <h4 className="font-body font-semibold text-gray-900">Features:</h4>
                  <ul className="space-y-1">
                    {step.features.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2 font-body text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="bg-blue-50 border-blue-200 mb-6">
          <CardHeader>
            <CardTitle className="font-display text-xl text-blue-800">
              Testing Options
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <h4 className="font-body font-semibold text-blue-800">Demo Mode (No Auth Required)</h4>
                <p className="font-body text-sm text-blue-700">
                  Click "Demo Mode" buttons above to test onboarding components without authentication.
                  Perfect for UI testing and development.
                </p>
              </div>
              <div>
                <h4 className="font-body font-semibold text-blue-800">With Auth (Full Experience)</h4>
                <p className="font-body text-sm text-blue-700">
                  Click "With Auth" to test the complete flow. After registration, check the browser console
                  for the verification code (e.g., "Generated verification code: abc123").
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blush-50 border-blush-200">
          <CardHeader>
            <CardTitle className="font-display text-xl text-blush-800">
              Key Features Implemented
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-body font-semibold text-blush-800">Layout Features:</h4>
                <ul className="space-y-1">
                  {[
                    'Consistent header and footer',
                    'Progress indicator with step visualization',
                    'Responsive design (mobile-first)',
                    'Back button functionality',
                    'Step validation to prevent skipping',
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2 font-body text-sm text-blush-700">
                      <Circle className="h-3 w-3 fill-current" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-body font-semibold text-blush-800">User Experience:</h4>
                <ul className="space-y-1">
                  {[
                    'Data preservation between steps',
                    'Visual progress tracking',
                    'Accessible keyboard navigation',
                    'Clear step titles and descriptions',
                    'Consistent styling with design system',
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2 font-body text-sm text-blush-700">
                      <Circle className="h-3 w-3 fill-current" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <Button asChild variant="outline">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
